// ---------------------------------------------------------------------------
// Fonts and sizing for the site-logo sub text (Branding → Site Logo → Sub Text,
// set in Admin > Branding here or in the corporate dashboard).
//
// The branding row stores the font KEY, not the stack, so the dashboard's
// preview and this app can each map it to a stack that is actually available to
// them. Only three web fonts are loaded by the app (Inter, Montserrat and
// Cinzel, see index.html) — everything else here is web-safe and installed on
// the machine, so the caption never falls back to something unintended.
// Copperplate was tried and removed for failing that test: macOS has it, most
// Windows machines do not, and it degraded to Georgia with nothing to warn you.
//
// A key that is not in this map, and the empty key, both mean "inherit the
// sidebar font", which is what every account rendered before the field existed.
// ---------------------------------------------------------------------------

export const LOGO_SUB_TEXT_FONTS: Record<string, string> = {
  inter: "Inter, ui-sans-serif, system-ui, sans-serif",
  montserrat: "Montserrat, 'Helvetica Neue', Helvetica, Arial, sans-serif",
  arial: "Arial, Helvetica, sans-serif",
  verdana: "Verdana, Geneva, sans-serif",
  tahoma: "Tahoma, Verdana, sans-serif",
  trebuchet: "'Trebuchet MS', Tahoma, sans-serif",
  georgia: "Georgia, 'Times New Roman', serif",
  times: "'Times New Roman', Times, serif",
  // Inscriptional Roman capitals — the engraved, formal look Copperplate was
  // wanted for. A web font (loaded in index.html) rather than Copperplate
  // itself, which ships only with macOS and with Office on Windows, so it
  // silently rendered as Georgia on most machines.
  cinzel: "Cinzel, 'Times New Roman', Times, serif",
  courier: "'Courier New', Courier, monospace",
};

/** The Font select's choices, in corp's order and wording. */
export const LOGO_SUB_TEXT_FONT_OPTIONS: { key: string; label: string }[] = [
  { key: "inter", label: "Inter" },
  { key: "montserrat", label: "Montserrat" },
  { key: "arial", label: "Arial" },
  { key: "verdana", label: "Verdana" },
  { key: "tahoma", label: "Tahoma" },
  { key: "trebuchet", label: "Trebuchet MS" },
  { key: "georgia", label: "Georgia" },
  { key: "times", label: "Times New Roman" },
  { key: "cinzel", label: "Cinzel" },
  { key: "courier", label: "Courier New" },
];

/** CSS stack for a stored font key, or undefined to inherit. */
export function logoSubTextFontStack(key: string | undefined): string | undefined {
  if (!key) return undefined;
  return LOGO_SUB_TEXT_FONTS[key.trim().toLowerCase()];
}

/**
 * Font size in px the caption falls back to when the account has not set one —
 * what it rendered at before the field existed.
 */
export const LOGO_SUB_TEXT_DEFAULT_SIZE_PX = 11;

/** Font size the caption should render at, in px. 0 / blank = the default. */
export function logoSubTextFontSize(sizePx: number | undefined): number {
  const px = sizePx && sizePx > 0 ? sizePx : LOGO_SUB_TEXT_DEFAULT_SIZE_PX;
  // Guard rails only — a stray value should not be able to blow out the header.
  return Math.min(64, Math.max(6, px));
}

/**
 * Letter spacing in px, negative tightening — useful for a long practice name
 * under a narrow logo.
 *
 * Unset and 0 both mean CSS `normal`: the navigation applies no tracking of its
 * own. The dashboard pre-fills 2 for a caption that has none yet, so a new
 * caption is saved WITH a value — but that is a starting point offered to the
 * person editing, not a default this side invents at render time.
 */
export function logoSubTextLetterSpacing(spacingPx: number | null | undefined): string {
  const px = Number(spacingPx);
  if (!Number.isFinite(px) || px === 0) return "normal";
  return `${Math.min(20, Math.max(-20, px))}px`;
}

/**
 * Caption colour as a CSS value, or undefined to keep the sidebar default
 * (white at 70%). Stored as 6 hex digits without the leading #, matching the
 * other branding colours.
 */
export function logoSubTextColor(hex: string | undefined): string | undefined {
  if (!hex) return undefined;
  const clean = hex.replace(/^#/, "").trim();
  return /^[0-9a-fA-F]{6}$/.test(clean) ? `#${clean}` : undefined;
}

/**
 * Vertical nudge in px, negative being up. Applied as a relative offset rather
 * than a margin so the header's height does not change as it moves — pushed far
 * enough up, the caption sits over the logo, which is the point.
 */
export function logoSubTextOffsetY(offsetY: number | undefined): number {
  const px = Number(offsetY);
  if (!Number.isFinite(px)) return 0;
  return Math.min(60, Math.max(-60, px));
}

/**
 * Letter spacing pre-filled for a caption with none stored yet (corp's starting
 * value). It is written on save; the navigation itself applies no tracking.
 */
export const LOGO_SUB_TEXT_DEFAULT_LETTER_SPACING_PX = 2;

/** Height the navigation draws the site logo at (h-10 in Sidebar.tsx); the preview must match. */
export const LOGO_SUB_TEXT_NAV_LOGO_HEIGHT_PX = 40;

export const LOGO_SUB_TEXT_ALIGNMENTS = ["left", "center", "right"] as const;
export type LogoSubTextAlign = (typeof LOGO_SUB_TEXT_ALIGNMENTS)[number];

/**
 * The caption's style is one JSON column (siteLogoSubTextStyle). The form keeps
 * the properties flat and as strings, as the inputs bind them; parse / serialise
 * convert at the edges — the same shape and rules as corp's
 * lib/logo-sub-text-fonts.ts, so either app can edit what the other saved.
 */
export interface LogoSubTextStyleForm {
  siteLogoSubTextAlign: string;
  siteLogoSubTextFont: string;
  siteLogoSubTextSize: string;
  siteLogoSubTextLetterSpacing: string;
  siteLogoSubTextColor: string;
  siteLogoSubTextOffsetY: string;
}

/** Stored blob (string, parsed object, or empty) -> flat form fields. */
export function parseLogoSubTextStyle(raw: unknown): LogoSubTextStyleForm {
  let style: Record<string, unknown> = {};
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") style = parsed as Record<string, unknown>;
    } catch {
      // Invalid JSON falls back to the defaults rather than breaking the form.
    }
  } else if (raw && typeof raw === "object") {
    style = raw as Record<string, unknown>;
  }

  const align = String(style.align ?? "").toLowerCase();
  return {
    siteLogoSubTextAlign: (LOGO_SUB_TEXT_ALIGNMENTS as readonly string[]).includes(align) ? align : "center",
    siteLogoSubTextFont: typeof style.font === "string" ? style.font : "",
    siteLogoSubTextSize: Number(style.size) > 0 ? String(Math.round(Number(style.size))) : "",
    siteLogoSubTextLetterSpacing:
      style.letterSpacing === undefined || style.letterSpacing === null || style.letterSpacing === ""
        ? String(LOGO_SUB_TEXT_DEFAULT_LETTER_SPACING_PX)
        : String(Number(style.letterSpacing)),
    siteLogoSubTextColor: typeof style.color === "string" ? style.color.replace(/^#/, "") : "",
    siteLogoSubTextOffsetY: Number(style.offsetY) ? String(Math.round(Number(style.offsetY))) : "",
  };
}

/**
 * Flat form fields -> the JSON string to store; only what the account chose.
 * A half-typed number ("-", "1.") counts as not set. An emptied spacing falls
 * back to the starting value, so a saved caption always carries one.
 */
export function serialiseLogoSubTextStyle(form: LogoSubTextStyleForm): string {
  const style: Record<string, string | number> = {};

  const align = form.siteLogoSubTextAlign;
  if ((LOGO_SUB_TEXT_ALIGNMENTS as readonly string[]).includes(align) && align !== "center") style.align = align;
  if (form.siteLogoSubTextFont) style.font = form.siteLogoSubTextFont;

  const size = Math.round(Number(form.siteLogoSubTextSize)) || 0;
  if (size > 0) style.size = Math.min(255, Math.max(1, size));

  const spacingRaw = form.siteLogoSubTextLetterSpacing.trim();
  const spacing = spacingRaw !== "" && Number.isFinite(Number(spacingRaw)) ? Number(spacingRaw) : LOGO_SUB_TEXT_DEFAULT_LETTER_SPACING_PX;
  style.letterSpacing = Math.min(20, Math.max(-20, spacing));

  const color = form.siteLogoSubTextColor.replace(/^#/, "").trim();
  if (/^[0-9a-fA-F]{6}$/.test(color)) style.color = color.toLowerCase();

  const offsetY = Math.round(Number(form.siteLogoSubTextOffsetY)) || 0;
  if (offsetY !== 0) style.offsetY = Math.min(60, Math.max(-60, offsetY));

  return Object.keys(style).length ? JSON.stringify(style) : "";
}
